/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

const STORAGE_KEY_PREFIX = 'ff_override_';

export interface FeatureFlagEntry {
  key: string;
  description: string;
  buildValue: boolean;
  runtimeOverride?: boolean;
  requiresDebug?: boolean;
  isMockToggle?: boolean;
}

const registry = new Map<string, FeatureFlagEntry>();

const isDebugMode = (): boolean => import.meta.env.DEBUG === 'true';

function parseBool(value: string | undefined, defaultValue: boolean): boolean {
  if (value === undefined || value === '') return defaultValue;
  const n = value.trim().toLowerCase();
  return n === 'true' || n === '1' || n === 'yes';
}

function readOverride(key: string): boolean | undefined {
  if (import.meta.env.DEBUG !== 'true') return undefined;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
    if (raw === null) return undefined;
    return raw === 'true';
  } catch {
    return undefined;
  }
}

export function defineFlag(
  key: string,
  envValue: string | undefined,
  defaultValue: boolean,
  description: string,
  options?: { requiresDebug?: boolean; isMockToggle?: boolean },
): boolean {
  const buildValue = parseBool(envValue, defaultValue);
  const override = readOverride(key);

  const entry: FeatureFlagEntry = {
    key,
    description,
    buildValue,
    runtimeOverride: override,
    requiresDebug: options?.requiresDebug,
    isMockToggle: options?.isMockToggle,
  };

  registry.set(key, entry);
  return getResolvedValue(entry);
}

function getResolvedValue(entry: FeatureFlagEntry): boolean {
  if (entry.requiresDebug && !isDebugMode()) return false;
  if (entry.runtimeOverride !== undefined) return entry.runtimeOverride;
  return entry.buildValue;
}

export function getFlag(key: string): boolean {
  const entry = registry.get(key);
  if (!entry) return false;
  return getResolvedValue(entry);
}

export function setFlagOverride(key: string, value: boolean): void {
  if (import.meta.env.DEBUG !== 'true') return;
  const entry = registry.get(key);
  if (!entry) return;
  entry.runtimeOverride = value;
  try {
    sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, String(value));
  } catch { /* quota exceeded or restricted */ }
}

export function clearOverride(key: string): void {
  if (import.meta.env.DEBUG !== 'true') return;
  const entry = registry.get(key);
  if (!entry) return;
  entry.runtimeOverride = undefined;
  try {
    sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
  } catch { /* restricted */ }
}

export function clearAllOverrides(): void {
  if (import.meta.env.DEBUG !== 'true') return;
  for (const entry of registry.values()) {
    entry.runtimeOverride = undefined;
    try {
      sessionStorage.removeItem(`${STORAGE_KEY_PREFIX}${entry.key}`);
    } catch { /* restricted */ }
  }
}

export function getAllFlags(): FeatureFlagEntry[] {
  if (import.meta.env.DEBUG !== 'true') return [];
  return Array.from(registry.values());
}

export function getNonMockFlags(): FeatureFlagEntry[] {
  return getAllFlags().filter((f) => !f.isMockToggle);
}

export function getMockFlags(): FeatureFlagEntry[] {
  return getAllFlags().filter((f) => f.isMockToggle);
}
